import React, { useState, useEffect } from 'react';
import {
  createFarmerData,
  GENDER_CHOICES,
  LAND_STATUS_CHOICES,
  SOCIAL_CATEGORY_CHOICES,
  LIVELIHOOD_CHOICES,
  CROPPING_SEASON_CHOICES,
  CROPPING_PATTERN_CHOICES,
  FARMING_PRACTICE_CHOICES,
  CROP_TYPE_CHOICES,
  SOIL_TYPE_CHOICES,
  SOIL_PH_CHOICES,
  FERTILIZER_TYPE_CHOICES,
  PESTICIDE_CATEGORY_CHOICES,
  ENERGY_USE_CHOICES,
  ENERGY_UNIT_CHOICES,
  WATER_SOURCE_CHOICES,
  IRRIGATION_METHOD_CHOICES,
  TRANSPORT_ACTIVITY_CHOICES,
  TRANSPORT_MODE_CHOICES,
  FUEL_TYPE_CHOICES
} from '../../../utils/farmerDataUtils';
import { externalApi } from '../../../services/api';

const FarmerForm = ({ farmer = null, onSubmit, onCancel, isSubmitting = false }) => {
  const [formData, setFormData] = useState(createFarmerData());
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pincodeLoading, setPincodeLoading] = useState(false);

  // Function to fetch pincode data from the API
  const fetchPincodeData = async (pincode) => {
    try {
      setPincodeLoading(true);
      return await externalApi.getPincodeData(pincode);
    } catch (error) {
      console.error('Error fetching pincode data:', error);
      return null;
    } finally {
      setPincodeLoading(false);
    }
  };

  useEffect(() => {
    if (farmer) {
      // Create a clean copy of the farmer data for the form
      const cleanedData = {
        // Farmer Information
        farmer_name: farmer.farmer_name || '',
        spouse_name: farmer.spouse_name || '',
        gender: farmer.gender || '',
        mobile: farmer.mobile || '',
        alt_mobile: farmer.alt_mobile || '',
        govt_id: farmer.govt_id || '',

        // Land Information
        acreage: farmer.acreage || '',
        land_status: farmer.land_status || '',
        product_ids: farmer.product_ids || '',

        // Location Information
        village: farmer.village || '',
        mandal: farmer.mandal || '',
        district: farmer.district || '',
        state: farmer.state || '',
        pincode: farmer.pincode || '',

        // Organization Information
        has_organization: farmer.has_organization || false,
        fpo_name: farmer.fpo_name || '',
        fpg_name: farmer.fpg_name || '',
        fpg_id: farmer.fpg_id || '',

        // Social Information
        category: farmer.category || '',
        literacy: farmer.literacy || '',
        children: farmer.children || '',
        livelihood: farmer.livelihood || '',
        annual_income: farmer.annual_income || '',
        other_source_income: farmer.other_source_income || '',
        total_income: farmer.total_income || '',

        // Crop Information
        crop_name: farmer.crop_name || '',
        crop_area: farmer.crop_area || '',
        crop_area_unit: farmer.crop_area_unit || 'acres',
        yield_produced: farmer.yield_produced || '',
        yield_unit: farmer.yield_unit || 'kg',
        cropping_season: farmer.cropping_season || '',
        cropping_pattern: farmer.cropping_pattern || '',
        farming_practice: farmer.farming_practice || '',
        agriculture_crop_type: farmer.agriculture_crop_type || '',

        // Soil Characteristics
        soil_type: farmer.soil_type || '',
        soil_ph: farmer.soil_ph || '',
        ec: farmer.ec || '',
        ec_unit: farmer.ec_unit || 'dS/m',
        organic_carbon: farmer.organic_carbon || '',
        organic_carbon_unit: farmer.organic_carbon_unit || '%',

        // Fertilizer
        fertilizer_type: farmer.fertilizer_type || '',
        application_rate: farmer.application_rate || '',
        application_rate_unit: farmer.application_rate_unit || 'kg/ha',

        // Crop Protection
        pesticide_category: farmer.pesticide_category || '',
        pesticide_application_rate: farmer.pesticide_application_rate || '',
        pesticide_application_rate_unit: farmer.pesticide_application_rate_unit || 'kg/ha',

        // Fuel & Energy
        direct_energy_use: farmer.direct_energy_use || '',
        energy_used: farmer.energy_used || '',
        energy_unit: farmer.energy_unit || 'kWh',
        energy_category: farmer.energy_category || '',

        // Irrigation
        water_source: farmer.water_source || '',
        irrigation_method: farmer.irrigation_method || '',
        power_source: farmer.power_source || '',
        power_consumption: farmer.power_consumption || '',
        power_consumption_unit: farmer.power_consumption_unit || 'kWh',

        // Transport
        transport_activity_type: farmer.transport_activity_type || '',
        transport_mode: farmer.transport_mode || '',
        distance: farmer.distance || '',
        distance_unit: farmer.distance_unit || 'km',
        fuel_type: farmer.fuel_type || '',
        fuel_consumption: farmer.fuel_consumption || '',
        fuel_consumption_unit: farmer.fuel_consumption_unit || 'lit',

        // For file fields, we'll keep the URLs but not as File objects
        // The form submission will handle these specially
        farmer_photo: farmer.farmer_photo || null,
        land_photo_1: farmer.land_photo_1 || null,
        land_photo_2: farmer.land_photo_2 || null,
        land_photo_3: farmer.land_photo_3 || null,
        land_photo_4: farmer.land_photo_4 || null, // Added land_photo_4
        soil_characteristics: farmer.soil_characteristics || null, // Corrected to soil_characteristics
        fertilizer_photos: farmer.fertilizer_photos || null,
        crop_protection_photos: farmer.crop_protection_photos || null
      };

      setFormData(cleanedData);
      console.log('Initialized form with farmer data:', cleanedData);
    }
  }, [farmer]);

  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target;

    let updatedData = { ...formData };

    // For number inputs, store the value as a string to prevent automatic conversion
    // This prevents issues with decimal points and leading zeros
    if (type === 'number') {
      updatedData[name] = value; // Store as string, not as number
    } else {
      updatedData[name] = type === 'checkbox' ? checked : value;
    }

    // Calculate total income when annual_income or other_source_income changes
    if (name === 'annual_income' || name === 'other_source_income') {
      const annualIncome = parseFloat(name === 'annual_income' ? value : formData.annual_income) || 0;
      const otherIncome = parseFloat(name === 'other_source_income' ? value : formData.other_source_income) || 0;
      updatedData.total_income = (annualIncome + otherIncome).toString();
      console.log(`Calculated total income: ${updatedData.total_income} (Annual: ${annualIncome}, Other: ${otherIncome})`);
    }

    // Auto-populate district and state when pincode changes
    if (name === 'pincode' && value.length === 6) {
      try {
        // Only fetch if it's a valid 6-digit pincode
        const pincodeData = await fetchPincodeData(value);
        if (pincodeData) {
          console.log('Fetched pincode data:', pincodeData);
          updatedData.district = pincodeData.district;
          updatedData.state = pincodeData.state;
        } else {
          // If no data found, show a message
          setError('No location data found for this pincode. Please enter district and state manually.');
          // Clear the error after 5 seconds
          setTimeout(() => setError(null), 5000);
        }
      } catch (error) {
        console.error('Error auto-populating location data:', error);
        setError('Error fetching location data. Please enter district and state manually.');
        // Clear the error after 5 seconds
        setTimeout(() => setError(null), 5000);
      }
    }

    setFormData(updatedData);
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    // Handle all file inputs the same way - store the File object
    if (files && files.length > 0) {
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);

      // Create a FormData object for the submission
      const formDataObj = new FormData();

      const mediaFieldKeys = [
        'farmer_photo',
        'land_photo_1',
        'land_photo_2',
        'land_photo_3',
        'land_photo_4',
        'soil_characteristics',
        'fertilizer_photos',
        'crop_protection_photos'
      ];

      // Add all form fields to the FormData
      Object.entries(formData).forEach(([key, value]) => {
        if (mediaFieldKeys.includes(key)) {
          // For designated media fields
          if (value instanceof File) { // Only append if it's a new file upload
            formDataObj.append(key, value);
          }
          // If 'value' is a string (URL of existing file) or null, do nothing for media fields.
          // The backend will keep the existing file if this field is not sent.
          // To clear an image, the backend would need a specific mechanism (e.g., sending 'key__clear': true)
          // which is not implemented here. For now, we only handle new uploads or keeping existing.
        } else { // For non-media fields
          if (value !== null && value !== undefined) {
            formDataObj.append(key, value);
          } else if (value === null && farmer && farmer[key] !== null) {
            // If the field was explicitly set to null by the user (and wasn't null before),
            // send an empty string to clear it on the backend for text/choice fields.
            formDataObj.append(key, '');
          }
          // If value is undefined, or was already null and not changed, don't append.
        }
      });

      console.log('Submitting form data (inspect for files):');
      for (let [key, val] of formDataObj.entries()) {
        console.log(key, val);
      }

      await onSubmit(formDataObj);

      setError(null);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(`Failed to submit form: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.formContainer}>
      <div style={styles.formHeader}>
        <h2 style={styles.title}>
          {farmer ? 'Edit Farmer' : 'Add New Farmer'}
        </h2>
        <button
          onClick={onCancel}
          style={styles.closeButton}
          title="Close"
          type="button"
        >
          ✕
        </button>
      </div>
      {error && (
        <div style={styles.errorMessage}>
          {error}
        </div>
      )}
      <form onSubmit={handleSubmit} style={styles.form}>

        {/* Farmer Information */}
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Farmer Information</legend>
          <div style={styles.formGroup}>
            <label htmlFor="farmer_name" style={styles.label}>Farmer Name</label>
            <input
              type="text"
              id="farmer_name"
              name="farmer_name"
              value={formData.farmer_name}
              onChange={handleChange}
              style={styles.input}
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="spouse_name" style={styles.label}>Spouse Name</label>
            <input
              type="text"
              id="spouse_name"
              name="spouse_name"
              value={formData.spouse_name}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="gender" style={styles.label}>Gender</label>
            <select
              id="gender"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              style={styles.input}
              required
            >
              <option value="">Select Gender</option>
              {GENDER_CHOICES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="mobile" style={styles.label}>Mobile</label>
            <input
              type="tel"
              id="mobile"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              style={styles.input}
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="alt_mobile" style={styles.label}>Alternate Mobile</label>
            <input
              type="tel"
              id="alt_mobile"
              name="alt_mobile"
              value={formData.alt_mobile}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="govt_id" style={styles.label}>Government ID</label>
            <input
              type="text"
              id="govt_id"
              name="govt_id"
              value={formData.govt_id}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
        </fieldset>

        {/* Land Information */}
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Land Information</legend>
          <div style={styles.formGroup}>
            <label htmlFor="acreage" style={styles.label}>Acreage</label>
            <input
              type="text"
              inputMode="decimal"
              id="acreage"
              name="acreage"
              value={formData.acreage}
              onChange={handleChange}
              style={styles.input}
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="land_status" style={styles.label}>Land Status</label>
            <select
              id="land_status"
              name="land_status"
              value={formData.land_status}
              onChange={handleChange}
              style={styles.input}
              required
            >
              <option value="">Select Status</option>
              {LAND_STATUS_CHOICES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="product_ids" style={styles.label}>Product IDs</label>
            <input
              type="text"
              id="product_ids"
              name="product_ids"
              value={formData.product_ids}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
        </fieldset>

        {/* Location Information */}
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Location Information</legend>
          <div style={styles.formGroup}>
            <label htmlFor="village" style={styles.label}>Village</label>
            <input
              type="text"
              id="village"
              name="village"
              value={formData.village}
              onChange={handleChange}
              style={styles.input}
              required
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="mandal" style={styles.label}>Mandal</label>
            <input
              type="text"
              id="mandal"
              name="mandal"
              value={formData.mandal}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="district" style={styles.label}>District</label>
            <div style={styles.inputWithIndicator}>
              <input
                type="text"
                id="district"
                name="district"
                value={formData.district}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  backgroundColor: pincodeLoading ? '#f0f8ff' : 'white',
                }}
                readOnly={pincodeLoading}
              />
              {pincodeLoading && <div style={styles.loadingIndicator}>Auto-filling...</div>}
            </div>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="state" style={styles.label}>State</label>
            <div style={styles.inputWithIndicator}>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  backgroundColor: pincodeLoading ? '#f0f8ff' : 'white',
                }}
                readOnly={pincodeLoading}
              />
              {pincodeLoading && <div style={styles.loadingIndicator}>Auto-filling...</div>}
            </div>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="pincode" style={styles.label}>Pincode</label>
            <div style={styles.inputWithIndicator}>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                id="pincode"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                style={styles.input}
                required
                placeholder="Enter 6-digit pincode"
              />
              {pincodeLoading && <div style={styles.loadingIndicator}>Loading...</div>}
            </div>
            <small style={styles.helperText}>
              Enter a 6-digit pincode to auto-fill district and state
            </small>
          </div>
        </fieldset>

        {/* Organization Information */}
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Organization Information</legend>
          <div style={styles.formGroup}>
            <label htmlFor="has_organization" style={styles.label}>
              Has Organization
            </label>
            <input
              type="checkbox"
              id="has_organization"
              name="has_organization"
              checked={formData.has_organization}
              onChange={handleChange}
              style={{ marginLeft: '10px' }}
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="fpo_name" style={styles.label}>FPO Name</label>
            <input
              type="text"
              id="fpo_name"
              name="fpo_name"
              value={formData.fpo_name}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="fpg_name" style={styles.label}>FPG Name</label>
            <input
              type="text"
              id="fpg_name"
              name="fpg_name"
              value={formData.fpg_name}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="fpg_id" style={styles.label}>FPG ID</label>
            <input
              type="text"
              id="fpg_id"
              name="fpg_id"
              value={formData.fpg_id}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
        </fieldset>

        {/* Social Information */}
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Social Information</legend>
          <div style={styles.formGroup}>
            <label htmlFor="category" style={styles.label}>Social Category</label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select Category</option>
              {SOCIAL_CATEGORY_CHOICES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Literacy</label>
            <div style={styles.radioGroup}>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="literacy"
                  value="yes"
                  checked={formData.literacy === "yes"}
                  onChange={handleChange}
                  style={styles.radioInput}
                />
                Yes
              </label>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="literacy"
                  value="no"
                  checked={formData.literacy === "no"}
                  onChange={handleChange}
                  style={styles.radioInput}
                />
                No
              </label>
            </div>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="children" style={styles.label}>Children</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              id="children"
              name="children"
              value={formData.children}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="livelihood" style={styles.label}>Livelihood</label>
            <select
              id="livelihood"
              name="livelihood"
              value={formData.livelihood}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select Livelihood</option>
              {LIVELIHOOD_CHOICES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="annual_income" style={styles.label}>Annual Income</label>
            <input
              type="text"
              inputMode="numeric"
              id="annual_income"
              name="annual_income"
              value={formData.annual_income}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="other_source_income" style={styles.label}>Other Source Income</label>
            <input
              type="text"
              inputMode="numeric"
              id="other_source_income"
              name="other_source_income"
              value={formData.other_source_income}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="total_income" style={styles.label}>Total Income (Auto-calculated)</label>
            <input
              type="text"
              inputMode="numeric"
              id="total_income"
              name="total_income"
              value={formData.total_income}
              readOnly
              style={{...styles.input, backgroundColor: '#f5f5f5', cursor: 'not-allowed'}}
            />
          </div>
        </fieldset>

        {/* Crop Information */}
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Crop Information</legend>
          <div style={styles.formGroup}>
            <label htmlFor="crop_name" style={styles.label}>Crop Name</label>
            <input
              type="text"
              id="crop_name"
              name="crop_name"
              value={formData.crop_name}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="crop_area" style={styles.label}>Crop Area</label>
            <input
              type="text"
              inputMode="decimal"
              id="crop_area"
              name="crop_area"
              value={formData.crop_area}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="crop_area_unit" style={styles.label}>Crop Area Unit</label>
            <select
              id="crop_area_unit"
              name="crop_area_unit"
              value={formData.crop_area_unit}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="acres">Acres</option>
              <option value="hectares">Hectares</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="yield_produced" style={styles.label}>Yield Produced</label>
            <input
              type="text"
              inputMode="decimal"
              id="yield_produced"
              name="yield_produced"
              value={formData.yield_produced}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="yield_unit" style={styles.label}>Yield Unit</label>
            <input
              type="text"
              id="yield_unit"
              name="yield_unit"
              value={formData.yield_unit}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="cropping_season" style={styles.label}>Cropping Season</label>
            <select
              id="cropping_season"
              name="cropping_season"
              value={formData.cropping_season}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select Season</option>
              {CROPPING_SEASON_CHOICES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="cropping_pattern" style={styles.label}>Cropping Pattern</label>
            <select
              id="cropping_pattern"
              name="cropping_pattern"
              value={formData.cropping_pattern}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select Cropping Pattern</option>
              {CROPPING_PATTERN_CHOICES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="farming_practice" style={styles.label}>Farming Practice</label>
            <select
              id="farming_practice"
              name="farming_practice"
              value={formData.farming_practice}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select Farming Practice</option>
              {FARMING_PRACTICE_CHOICES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="agriculture_crop_type" style={styles.label}>Agriculture Crop Type</label>
            <select
              id="agriculture_crop_type"
              name="agriculture_crop_type"
              value={formData.agriculture_crop_type}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select Crop Type</option>
              {CROP_TYPE_CHOICES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </fieldset>

        {/* Soil Characteristics */}
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Soil Characteristics</legend>
          <div style={styles.formGroup}>
            <label htmlFor="soil_type" style={styles.label}>Soil Type</label>
            <select
              id="soil_type"
              name="soil_type"
              value={formData.soil_type}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select Soil Type</option>
              {SOIL_TYPE_CHOICES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="soil_ph" style={styles.label}>Soil pH</label>
            <select
              id="soil_ph"
              name="soil_ph"
              value={formData.soil_ph}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select Soil pH</option>
              {SOIL_PH_CHOICES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="ec" style={styles.label}>EC (dS/m)</label>
            <input
              type="text"
              inputMode="decimal"
              id="ec"
              name="ec"
              value={formData.ec}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="organic_carbon" style={styles.label}>Organic Carbon (%)</label>
            <input
              type="text"
              inputMode="decimal"
              id="organic_carbon"
              name="organic_carbon"
              value={formData.organic_carbon}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
        </fieldset>

        {/* Fertilizer */}
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Fertilizer</legend>
          <div style={styles.formGroup}>
            <label htmlFor="fertilizer_type" style={styles.label}>Fertilizer Type</label>
            <select
              id="fertilizer_type"
              name="fertilizer_type"
              value={formData.fertilizer_type}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select Fertilizer Type</option>
              {FERTILIZER_TYPE_CHOICES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="application_rate" style={styles.label}>Application Rate</label>
            <input
              type="text"
              inputMode="decimal"
              id="application_rate"
              name="application_rate"
              value={formData.application_rate}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="application_rate_unit" style={styles.label}>Application Rate Unit</label>
            <select
              id="application_rate_unit"
              name="application_rate_unit"
              value={formData.application_rate_unit}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="kg/ha">kg/ha</option>
            </select>
          </div>
        </fieldset>

        {/* Crop Protection */}
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Crop Protection</legend>
          <div style={styles.formGroup}>
            <label htmlFor="pesticide_category" style={styles.label}>Pesticide Category</label>
            <select
              id="pesticide_category"
              name="pesticide_category"
              value={formData.pesticide_category}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select Pesticide Category</option>
              {PESTICIDE_CATEGORY_CHOICES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="pesticide_application_rate" style={styles.label}>Pesticide Application Rate</label>
            <input
              type="text"
              inputMode="decimal"
              id="pesticide_application_rate"
              name="pesticide_application_rate"
              value={formData.pesticide_application_rate}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="pesticide_application_rate_unit" style={styles.label}>Rate Unit</label>
            <select
              id="pesticide_application_rate_unit"
              name="pesticide_application_rate_unit"
              value={formData.pesticide_application_rate_unit}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="kg/ha">kg/ha</option>
            </select>
          </div>
        </fieldset>

        {/* Fuel & Energy */}
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Fuel & Energy</legend>
          <div style={styles.formGroup}>
            <label htmlFor="direct_energy_use" style={styles.label}>Direct Energy Use</label>
            <select
              id="direct_energy_use"
              name="direct_energy_use"
              value={formData.direct_energy_use}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select Energy Type</option>
              {ENERGY_USE_CHOICES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="energy_used" style={styles.label}>Energy Used</label>
            <input
              type="text"
              inputMode="decimal"
              id="energy_used"
              name="energy_used"
              value={formData.energy_used}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="energy_unit" style={styles.label}>Energy Unit</label>
            <select
              id="energy_unit"
              name="energy_unit"
              value={formData.energy_unit}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select Unit</option>
              {ENERGY_UNIT_CHOICES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="energy_category" style={styles.label}>Energy Category</label>
            <input
              type="text"
              id="energy_category"
              name="energy_category"
              value={formData.energy_category}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
        </fieldset>

        {/* Irrigation */}
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Irrigation</legend>
          <div style={styles.formGroup}>
            <label htmlFor="water_source" style={styles.label}>Water Source</label>
            <select
              id="water_source"
              name="water_source"
              value={formData.water_source}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select Water Source</option>
              {WATER_SOURCE_CHOICES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="irrigation_method" style={styles.label}>Irrigation Method</label>
            <select
              id="irrigation_method"
              name="irrigation_method"
              value={formData.irrigation_method}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select Method</option>
              {IRRIGATION_METHOD_CHOICES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="power_source" style={styles.label}>Power Source</label>
            <select
              id="power_source"
              name="power_source"
              value={formData.power_source}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select Power Source</option>
              {ENERGY_USE_CHOICES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="power_consumption" style={styles.label}>Power Consumption</label>
            <input
              type="text"
              inputMode="decimal"
              id="power_consumption"
              name="power_consumption"
              value={formData.power_consumption}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="power_consumption_unit" style={styles.label}>Unit</label>
            <select
              id="power_consumption_unit"
              name="power_consumption_unit"
              value={formData.power_consumption_unit}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select Unit</option>
              {ENERGY_UNIT_CHOICES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </fieldset>

        {/* Transport */}
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Transport</legend>
          <div style={styles.formGroup}>
            <label htmlFor="transport_activity_type" style={styles.label}>Transport Activity Type</label>
            <select
              id="transport_activity_type"
              name="transport_activity_type"
              value={formData.transport_activity_type}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select Activity Type</option>
              {TRANSPORT_ACTIVITY_CHOICES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="transport_mode" style={styles.label}>Transport Mode</label>
            <select
              id="transport_mode"
              name="transport_mode"
              value={formData.transport_mode}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select Mode</option>
              {TRANSPORT_MODE_CHOICES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="distance" style={styles.label}>Distance</label>
            <input
              type="text"
              inputMode="decimal"
              id="distance"
              name="distance"
              value={formData.distance}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="distance_unit" style={styles.label}>Distance Unit</label>
            <select
              id="distance_unit"
              name="distance_unit"
              value={formData.distance_unit}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="km">km</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="fuel_type" style={styles.label}>Fuel Type</label>
            <select
              id="fuel_type"
              name="fuel_type"
              value={formData.fuel_type}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select Fuel</option>
              {FUEL_TYPE_CHOICES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="fuel_consumption" style={styles.label}>Fuel Consumption</label>
            <input
              type="text"
              inputMode="decimal"
              id="fuel_consumption"
              name="fuel_consumption"
              value={formData.fuel_consumption}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="fuel_consumption_unit" style={styles.label}>Consumption Unit</label>
            <select
              id="fuel_consumption_unit"
              name="fuel_consumption_unit"
              value={formData.fuel_consumption_unit}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="lit">lit</option>
            </select>
          </div>
        </fieldset>

        {/* Media Files */}
        <fieldset style={styles.fieldset}>
          <legend style={styles.legend}>Media Files</legend>
          <div style={styles.formGroup}>
            <label htmlFor="farmer_photo" style={styles.label}>Farmer Photo</label>
            {formData.farmer_photo && typeof formData.farmer_photo === 'string' && (
              <div style={styles.existingFile}>
                <img
                  src={formData.farmer_photo.startsWith('http')
                    ? formData.farmer_photo
                    : `http://127.0.0.1:8000${formData.farmer_photo}`}
                  alt="Farmer"
                  style={styles.thumbnailPreview}
                />
                <span>Current photo</span>
              </div>
            )}
            <input
              type="file"
              id="farmer_photo"
              name="farmer_photo"
              onChange={handleFileChange}
              style={styles.input}
              accept="image/*"
            />
            <small style={styles.helperText}>
              {formData.farmer_photo && typeof formData.farmer_photo === 'string'
                ? 'Upload a new photo to replace the current one'
                : 'Upload a photo of the farmer'}
            </small>
          </div>
          {/* Land Photos - Individual fields for Django model */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Land Photos (up to 4)</label>

            <div style={styles.photoGroup}>
              <div style={styles.photoItem}>
                <label htmlFor="land_photo_1" style={styles.photoLabel}>Photo 1</label>
                {formData.land_photo_1 && typeof formData.land_photo_1 === 'string' && (
                  <div style={styles.existingFile}>
                    <img
                      src={formData.land_photo_1.startsWith('http')
                        ? formData.land_photo_1
                        : `http://127.0.0.1:8000${formData.land_photo_1}`}
                      alt="Land 1"
                      style={styles.thumbnailPreview}
                    />
                  </div>
                )}
                <input
                  type="file"
                  id="land_photo_1"
                  name="land_photo_1"
                  onChange={handleFileChange}
                  style={styles.input}
                  accept="image/*"
                />
              </div>

              <div style={styles.photoItem}>
                <label htmlFor="land_photo_2" style={styles.photoLabel}>Photo 2</label>
                {formData.land_photo_2 && typeof formData.land_photo_2 === 'string' && (
                  <div style={styles.existingFile}>
                    <img
                      src={formData.land_photo_2.startsWith('http')
                        ? formData.land_photo_2
                        : `http://127.0.0.1:8000${formData.land_photo_2}`}
                      alt="Land 2"
                      style={styles.thumbnailPreview}
                    />
                  </div>
                )}
                <input
                  type="file"
                  id="land_photo_2"
                  name="land_photo_2"
                  onChange={handleFileChange}
                  style={styles.input}
                  accept="image/*"
                />
              </div>

              <div style={styles.photoItem}>
                <label htmlFor="land_photo_3" style={styles.photoLabel}>Photo 3</label>
                {formData.land_photo_3 && typeof formData.land_photo_3 === 'string' && (
                  <div style={styles.existingFile}>
                    <img
                      src={formData.land_photo_3.startsWith('http')
                        ? formData.land_photo_3
                        : `http://127.0.0.1:8000${formData.land_photo_3}`}
                      alt="Land 3"
                      style={styles.thumbnailPreview}
                    />
                  </div>
                )}
                <input
                  type="file"
                  id="land_photo_3"
                  name="land_photo_3"
                  onChange={handleFileChange}
                  style={styles.input}
                  accept="image/*"
                />
              </div>

              <div style={styles.photoItem}>
                <label htmlFor="land_photo_4" style={styles.photoLabel}>Photo 4</label>
                {formData.land_photo_4 && typeof formData.land_photo_4 === 'string' && (
                  <div style={styles.existingFile}>
                    <img
                      src={formData.land_photo_4.startsWith('http')
                        ? formData.land_photo_4
                        : `http://127.0.0.1:8000${formData.land_photo_4}`}
                      alt="Land 4"
                      style={styles.thumbnailPreview}
                    />
                  </div>
                )}
                <input
                  type="file"
                  id="land_photo_4"
                  name="land_photo_4"
                  onChange={handleFileChange}
                  style={styles.input}
                  accept="image/*"
                />
              </div>
            </div>

            <small style={styles.helperText}>Upload up to 4 land photos</small>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="soil_characteristics" style={styles.label}>Soil Characteristics Photo</label>
            {formData.soil_characteristics && typeof formData.soil_characteristics === 'string' && (
              <div style={styles.existingFile}>
                <img
                  src={formData.soil_characteristics.startsWith('http')
                    ? formData.soil_characteristics
                    : `http://127.0.0.1:8000${formData.soil_characteristics}`}
                  alt="Soil Characteristics"
                  style={styles.thumbnailPreview}
                />
                <span>Current photo</span>
              </div>
            )}
            <input
              type="file"
              id="soil_characteristics"
              name="soil_characteristics"
              onChange={handleFileChange}
              style={styles.input}
              accept="image/*"
            />
            <small style={styles.helperText}>
              {formData.soil_characteristics && typeof formData.soil_characteristics === 'string'
                ? 'Upload a new photo to replace the current one'
                : 'Upload a photo of soil characteristics'}
            </small>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="fertilizer_photos" style={styles.label}>Fertilizer Photos</label>
            {formData.fertilizer_photos && typeof formData.fertilizer_photos === 'string' && (
              <div style={styles.existingFile}>
                <img
                  src={formData.fertilizer_photos.startsWith('http')
                    ? formData.fertilizer_photos
                    : `http://127.0.0.1:8000${formData.fertilizer_photos}`}
                  alt="Fertilizer"
                  style={styles.thumbnailPreview}
                />
                <span>Current photo</span>
              </div>
            )}
            <input
              type="file"
              id="fertilizer_photos"
              name="fertilizer_photos"
              onChange={handleFileChange}
              style={styles.input}
              accept="image/*"
            />
            <small style={styles.helperText}>
              {formData.fertilizer_photos && typeof formData.fertilizer_photos === 'string'
                ? 'Upload a new photo to replace the current one'
                : 'Upload photos of fertilizers used'}
            </small>
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="crop_protection_photos" style={styles.label}>Crop Protection Photos</label>
            {formData.crop_protection_photos && typeof formData.crop_protection_photos === 'string' && (
              <div style={styles.existingFile}>
                <img
                  src={formData.crop_protection_photos.startsWith('http')
                    ? formData.crop_protection_photos
                    : `http://127.0.0.1:8000${formData.crop_protection_photos}`}
                  alt="Crop Protection"
                  style={styles.thumbnailPreview}
                />
                <span>Current photo</span>
              </div>
            )}
            <input
              type="file"
              id="crop_protection_photos"
              name="crop_protection_photos"
              onChange={handleFileChange}
              style={styles.input}
              accept="image/*"
            />
            <small style={styles.helperText}>
              {formData.crop_protection_photos && typeof formData.crop_protection_photos === 'string'
                ? 'Upload a new photo to replace the current one'
                : 'Upload photos of crop protection measures'}
            </small>
          </div>
        </fieldset>

        <div style={styles.buttonGroup}>
          <button
            type="button"
            onClick={onCancel}
            style={styles.cancelButton}
            disabled={isSubmitting || submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            style={styles.submitButton}
            disabled={isSubmitting || submitting}
          >
            {(isSubmitting || submitting)
              ? (farmer ? 'Updating...' : 'Adding...')
              : (farmer ? 'Update' : 'Add') + ' Farmer'
            }
          </button>
        </div>
      </form>
    </div>
  );
};

const styles = {
  formContainer: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    maxWidth: '800px',
    margin: '0 auto',
  },
  formHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  title: {
    marginTop: 0,
    marginBottom: 0,
    color: '#2c3e50',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    color: '#666',
    cursor: 'pointer',
    padding: '0.5rem',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    transition: 'background-color 0.2s',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  fieldset: {
    border: '1px solid #ddd',
    borderRadius: '4px',
    padding: '1rem',
    marginBottom: '1rem'
  },
  legend: {
    padding: '0 0.5rem',
    fontWeight: 'bold',
    color: '#2c3e50'
  },
  formGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '4px',
    border: '1px solid #ddd',
    fontSize: '1rem',
  },
  inputWithIndicator: {
    position: 'relative',
    width: '100%',
  },
  loadingIndicator: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '0.8rem',
    color: '#3498db',
    fontWeight: 'bold',
  },
  helperText: {
    display: 'block',
    marginTop: '0.25rem',
    fontSize: '0.8rem',
    color: '#7f8c8d',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '1rem',
  },
  cancelButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#95a5a6',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },
  submitButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#2ecc71',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },
  errorMessage: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '1rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    border: '1px solid #ef9a9a',
  },
  existingFile: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '0.5rem',
    gap: '0.5rem',
  },
  thumbnailPreview: {
    width: '50px',
    height: '50px',
    objectFit: 'cover',
    borderRadius: '4px',
    border: '1px solid #ddd',
  },
  photoGroup: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '1rem',
  },
  photoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  photoLabel: {
    fontWeight: 'bold',
    fontSize: '0.9rem',
    color: '#2c3e50',
  },
  radioGroup: {
    display: 'flex',
    gap: '1.5rem',
    marginTop: '0.5rem',
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '1rem',
    color: '#2c3e50',
  },
  radioInput: {
    marginRight: '0.5rem',
    cursor: 'pointer',
  },
};

export default FarmerForm;





